import React, { useState } from 'react';
import { Text } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import 'react-phone-number-input/style.css';
import en from 'react-phone-number-input/locale/en.json';
import fr from 'react-phone-number-input/locale/fr.json';
import nl from 'react-phone-number-input/locale/nl.json';
import PhoneInput, {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from 'react-phone-number-input';
import { useFormContext } from 'react-hook-form';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import useAuth from '../../../../../../app/src/features/authentication/hooks/useAuth';
import { useLocale } from '../../../../../../../i18n';

interface Props {
  question: FlowsQuestion;
}

const PhoneInputComponent: React.FC<React.PropsWithChildren<Props>> = ({
  question,
}) => {
  const { t } = useTranslation();
  const { userLang } = useLocale();
  const { distribution } = useAuth();

  const { setError, clearErrors } = useFormContext();
  const {
    field: { onBlur, value },
    fieldState: { isTouched },
    error,
  } = useFlowInputComponent({ question });

  const [phoneNumber, setPhoneNumber] = useState<string>(value as string);

  const labelLang = {
    nl: { ...nl, BQ: t('COUNTRY_BQ_BONAIRE') },
    fr: { ...fr, BQ: t('COUNTRY_BQ_BONAIRE') },
    en: { ...en, BQ: t('COUNTRY_BQ_BONAIRE') },
  };

  const validatePhoneNumber = (phoneNumber: string) => {
    if (phoneNumber) {
      if (!isValidPhoneNumber(phoneNumber) && phoneNumber.length > 5) {
        setError(question.id, {
          type: 'manual',
          message: t(
            'Invalid telephone number Please use the international format',
          ),
        });
      } else if (error) {
        clearErrors(question.id);
      }
    }
  };

  const handleOnChange = (number: string) => {
    setPhoneNumber(number);
    if (isTouched) validatePhoneNumber(number);
  };

  const handleOnBlur = () => {
    if (!isTouched) {
      validatePhoneNumber(phoneNumber);
    }

    if (phoneNumber) {
      onBlur({
        name: question.id,
        value: formatPhoneNumberIntl(phoneNumber).replaceAll(' ', ''),
      });
    } else {
      onBlur({ name: question.id, value: undefined });
    }
  };

  const getDefaultCountry = () => {
    const whiteList = question.rules.address?.allowed_countries;

    const distriCountry = distribution.address?.country_code?.toUpperCase();
    if (whiteList?.length) {
      if (distriCountry && whiteList.includes(distriCountry)) {
        return distriCountry;
      }
      return whiteList[0];
    }
    return distriCountry || 'BE';
  };

  return (
    <>
      <StyledPhoneInput
        countries={question.rules.address?.allowed_countries}
        labels={labelLang[userLang.toLowerCase()] || en}
        international
        withCountryCallingCode
        placeholder={t('Enter phone number')}
        value={phoneNumber}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        defaultCountry={getDefaultCountry()}
        data-test-id="ProdFac_PhoneInputComponent"
        error={error?.message}
        isDisabled={question.rules.is_disabled}
        disabled={question.rules.is_disabled}
      />
      {error && (
        <Text
          mt="2"
          textAlign="right"
          fontSize="xs"
          color="danger.main"
          data-test-id="ProdFac_PhoneInputComponent_ErrorMessage"
        >
          {error?.message}
        </Text>
      )}
    </>
  );
};

const StyledPhoneInput = styled(PhoneInput)<{
  error?: boolean;
  isDisabled?: boolean;
}>`
  background-color: white;
  overflow: hidden;
  height: 4.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
  border: ${({ error, theme }) =>
    error ? `1px solid ${theme.status.error}` : '1px solid #e4e4e4'};
  border-radius: 4px;

  ${({ prefilled }) =>
    prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}

  .PhoneInputCountry,
  .PhoneInputInput {
    height: 100%;
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};
  }

  .PhoneInputCountry {
    margin: 0;
    padding: 0 1.2rem;
    background-color: #e4e4e4;
  }

  .PhoneInputInput {
    padding-left: 0.8rem;
    cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'default')};

    &:focus {
      outline-color: ${({ theme }) => theme.primary[500]};
    }
  }

  opacity: ${({ isDisabled }) => (isDisabled ? '0.4' : '1')};
`;

export default PhoneInputComponent;
